import { graphHooks } from '@tunasong/graph-lib/react'
import { Close, Left, Search } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isFolderContainer } from '@tunasong/schemas'
import { Button, CloseButton, IconButton, TunaDialog, disableAutocomplete, useDimensions } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, KeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import styles from './move-picker.module.css'
import { FolderLevel, SearchLevel, TopLevel } from './move-picker-levels.js'
import type { MoveHereState, Trail } from './move-picker-state.js'
import { crumbs, destinationPath, moveHere, moveHereLabel, pickerTitle, readOnlyNotice } from './move-picker-state.js'

export interface MovePickerDialogProps {
  /** What is being moved; the read-only ones are explained and left out */
  items: Persisted<Entity>[]
  /** Move here, into the trail's last folder, or Unfiled when it is empty */
  onMove(trail: Trail): void
  onCancel(): void
}

/** The rows the arrow keys move between, in order (folder rows, New folder, Try again) */
const rowsIn = (list: HTMLElement | null) => [...(list?.querySelectorAll<HTMLElement>('[data-row]') ?? [])]

/**
 * The Move picker (#2543): search, a list you open folder by folder, a breadcrumb trail, and Move here into the folder
 * you are in. Joni's canvas: https://claude.ai/artifact/28JiKUaiCvbKecRqVnRTPe
 */
export const MovePickerDialog: FC<MovePickerDialogProps> = props => {
  const { items, onMove, onCancel } = props
  const { userId } = useCurrentUser()
  const { isSmallOrSmaller: phone } = useDimensions()
  const [trail, setTrail] = useState<Trail>([])
  const [query, setQuery] = useState('')
  const titleId = useId()
  const reasonId = useId()
  const searchId = useId()
  const searchRef = useRef<HTMLInputElement>(null)

  /**
   * The destination as loaded: a folder reached from search or Recent arrives as a summary with no ACLs, and its
   * children may still be loading or have failed. Until both are known, Move here waits (#2543 M2, M3).
   */
  const folder = trail.at(-1)
  const { entity: loadedFolder } = graphHooks.useEntity(folder?.id)
  const { isLoading: levelLoading, isError: levelFailed } = graphHooks.useEntityChildren({
    parentId: folder?.id,
    filter: isFolderContainer,
  })
  const waiting = Boolean(folder && (!loadedFolder || levelLoading || levelFailed))
  const judged = folder && loadedFolder ? [...trail.slice(0, -1), loadedFolder] : trail
  const state: MoveHereState | { enabled: false; reason?: undefined } = waiting
    ? { enabled: false }
    : moveHere({ items, trail: judged, userId })
  const notice = readOnlyNotice(items, userId)

  /**
   * A level change replaces the row that had focus (WCAG 2.4.3, Joni on #2556): each one moves focus to the new
   * level's first row, or to the list while it has none (empty, or still loading). Opening the picker focuses search.
   */
  const listRef = useRef<HTMLDivElement>(null)
  const [levelChanges, setLevelChanges] = useState(0)
  useEffect(() => {
    if (levelChanges === 0) {
      return
    }
    const list = listRef.current
    ;(rowsIn(list)[0] ?? list)?.focus()
  }, [levelChanges])

  const go = (next: Trail) => {
    setTrail(next)
    setQuery('')
    setLevelChanges(count => count + 1)
  }
  const handleUp = () => go(trail.slice(0, -1))
  const handleCrumb = (depth: number) => () => go(trail.slice(0, depth))
  const handleMove = () => {
    if (state.enabled) {
      onMove(judged)
    }
  }

  /** ↓ from search reaches the first row; the arrows, Home and End move between rows; ↑ from the first goes back */
  const moveFocus = (ev: KeyboardEvent<HTMLDivElement>) => {
    const target = ev.target as HTMLElement
    const rows = rowsIn(listRef.current)
    const inSearch = target === searchRef.current
    const at = rows.indexOf(target)
    if (!(inSearch || at >= 0) || rows.length === 0) {
      return false
    }
    const next =
      ev.key === 'ArrowDown'
        ? inSearch
          ? rows[0]
          : rows[Math.min(at + 1, rows.length - 1)]
        : ev.key === 'ArrowUp' && !inSearch
          ? at === 0
            ? searchRef.current
            : rows[at - 1]
          : ev.key === 'Home' && !inSearch
            ? rows[0]
            : ev.key === 'End' && !inSearch
              ? rows.at(-1)
              : undefined
    next?.focus()
    return Boolean(next)
  }

  /**
   * Ctrl/Cmd+Enter is Move here from anywhere; Alt+↑ goes up a level; Backspace does too, but only from a row or a
   * crumb, so it never changes the destination under Move here (Joni, #2556).
   */
  const handleKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    const target = ev.target as HTMLElement
    if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault()
      handleMove()
      return
    }
    const fromRowOrCrumb = target.hasAttribute('data-row') || target.hasAttribute('data-crumb')
    const up = (ev.key === 'ArrowUp' && ev.altKey) || (ev.key === 'Backspace' && fromRowOrCrumb)
    if (up) {
      if (trail.length > 0) {
        ev.preventDefault()
        handleUp()
      }
      return
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(ev.key) && moveFocus(ev)) {
      ev.preventDefault()
    }
  }

  return (
    <TunaDialog
      open
      onClose={onCancel}
      fullScreen={phone}
      maxWidth={false}
      className={cn(styles.dialog, { [styles.phone]: phone })}
      aria-labelledby={titleId}
      initialFocus={searchRef}
    >
      <div className={styles.root} onKeyDown={handleKeyDown}>
        <div className={styles.header}>
          {phone && trail.length > 0 ? (
            <IconButton aria-label="Up a level" onClick={handleUp} className={styles.headerButton}>
              <Left />
            </IconButton>
          ) : null}
          <h2 id={titleId} className={styles.title}>
            {pickerTitle(items)}
          </h2>
          <CloseButton onClose={onCancel} />
        </div>

        <div className={styles.search}>
          <label htmlFor={searchId} className={styles.visuallyHidden}>
            Search folders
          </label>
          <div className={styles.searchField}>
            <Search className={styles.searchIcon} aria-hidden="true" />
            <input
              {...disableAutocomplete}
              ref={searchRef}
              id={searchId}
              type="search"
              className={styles.searchInput}
              placeholder="Search folders"
              autoFocus
              value={query}
              onChange={ev => setQuery(ev.target.value)}
            />
            {query ? (
              <button type="button" className={styles.clear} aria-label="Clear search" onClick={() => setQuery('')}>
                <Close aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>

        {notice ? (
          <p className={styles.notice} role="note">
            {notice}
          </p>
        ) : null}

        {trail.length > 0 && !query ? (
          <nav aria-label="Location" className={styles.crumbs}>
            {crumbs(trail).map((crumb, index, all) =>
              index === all.length - 1 ? (
                <span key={crumb.depth} aria-current="page" className={styles.crumbCurrent}>
                  {crumb.label}
                </span>
              ) : (
                <span key={crumb.depth} className={styles.crumbItem}>
                  <button type="button" data-crumb="" className={styles.crumb} onClick={handleCrumb(crumb.depth)}>
                    {crumb.label}
                  </button>
                  <span aria-hidden="true" className={styles.crumbSeparator}>
                    ›
                  </span>
                </span>
              )
            )}
          </nav>
        ) : null}

        <div
          className={styles.list}
          ref={listRef}
          tabIndex={-1}
          data-move-picker-list=""
          aria-busy={Boolean(folder && levelLoading)}
        >
          {query ? (
            <SearchLevel query={query} items={items} onGo={go} />
          ) : folder ? (
            <FolderLevel key={folder.id} trail={trail} items={items} onGo={go} />
          ) : (
            <TopLevel items={items} onGo={go} />
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.to}>
            To: <strong className={styles.toPath}>{destinationPath(trail)}</strong>
            {!state.enabled && state.reason ? (
              <span id={reasonId} className={styles.reason}>
                {state.reason}
              </span>
            ) : null}
          </span>
          {phone ? null : (
            <Button variant="outlined" color="inherit" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            className={styles.moveHere}
            disabled={!state.enabled}
            aria-describedby={!state.enabled && state.reason ? reasonId : undefined}
            onClick={handleMove}
          >
            {moveHereLabel(items, state.enabled ? state.count : undefined)}
          </Button>
        </div>
      </div>
    </TunaDialog>
  )
}
